'use client';

import { c as _c } from "react/compiler-runtime";
import { CHART, CHART_RELAYOUT_MS, NUMBERS, SCRIM_FADE_OUT_MS, STAGE_BUTTON, TRADES3, TRADES4, TRADE_LIFE_MS, easeInOutCubic, easeOutCubic, findSplitFraction, getStageDurationMs, linear, smoothPath, yFor, yesShareAt } from "./chunk-IZITNQXA.js";

// src/render-boundary.tsx
import { Component } from "react";
var RenderBoundary = class extends Component {
  state = {
    failed: false
  };
  static getDerivedStateFromError() {
    return {
      failed: true
    };
  }
  componentDidCatch(error, _info) {
    this.props.onFailure(error.name);
  }
  render() {
    return this.state.failed ? null : this.props.children;
  }
};

// src/simulated-trade-stage.tsx
import { useEffect, useLayoutEffect as useLayoutEffect2, useRef as useRef2, useState } from "react";

// src/presentation.tsx
import { createContext, useContext } from "react";
var PresentationContext = createContext(null);
var PresentationProvider = PresentationContext.Provider;
function usePresentation() {
  const value = useContext(PresentationContext);
  if (value === null) {
    throw new Error("Missing walkthrough presentation adapter");
  }
  return value;
}

// src/chart-playback.ts
function createChart(q, qa, jumping, isDestroyed, frames) {
  const chartRow = q("row-chart");
  const svg = q("chart");
  const targetLine = q("target");
  const badge = q("badge");
  const gridLines = qa("grid-line");
  const axisLabels = qa("axis-label");
  const seal = q("seal");
  const hit = q("hit");
  const endX = CHART.xs[CHART.xs.length - 1] ?? 440;
  const lines = {
    no: {
      path: q("line-no"),
      tip: q("tip-no"),
      len: 0,
      split: 0.5,
      f: 0
    },
    yes: {
      path: q("line-yes"),
      tip: q("tip-yes"),
      len: 0,
      split: 0.5,
      f: 0
    }
  };
  let chartH = 150;
  const y = p => yFor(p, chartH);
  const place = (el, px, py) => {
    el.style.left = `${px / CHART.viewWidth * 100}%`;
    el.style.top = `${py / chartH * 100}%`;
  };
  function applyReveal(key) {
    const l = lines[key];
    l.path.style.strokeDashoffset = `${l.len * (1 - l.f)}`;
    const p = l.path.getPointAtLength(l.len * l.f);
    place(l.tip, p.x, p.y);
  }
  function setReveal(key, f) {
    lines[key].f = f;
    applyReveal(key);
  }
  function layoutChart() {
    const h = chartRow.clientHeight;
    chartH = h > 40 ? h : 150;
    svg.setAttribute("viewBox", `0 0 ${CHART.viewWidth} ${chartH}`);
    const ty = y(CHART.target);
    targetLine.setAttribute("y1", `${ty}`);
    targetLine.setAttribute("y2", `${ty}`);
    badge.style.top = `${ty / chartH * 100}%`;
    CHART.axisTicks.forEach((p, i) => {
      const yy = y(p);
      gridLines[i]?.setAttribute("y1", `${yy}`);
      gridLines[i]?.setAttribute("y2", `${yy}`);
      const label = axisLabels[i];
      if (label !== void 0) label.style.top = `${yy / chartH * 100}%`;
    });
    for (const key of ["no", "yes"]) {
      const l = lines[key];
      l.path.setAttribute("d", smoothPath(CHART.xs, CHART[key].map(y)));
      l.len = l.path.getTotalLength();
      l.path.style.strokeDasharray = `${l.len}`;
      const splitX = CHART.xs[CHART.splitIndex] ?? 0;
      l.split = findSplitFraction(f => l.path.getPointAtLength(l.len * f).x, splitX);
      applyReveal(key);
    }
    place(seal, endX, ty);
    place(hit, endX, ty);
  }
  function relayoutFor(ms) {
    if (jumping()) {
      layoutChart();
      return;
    }
    const start = performance.now();
    const step = now => {
      if (isDestroyed()) return;
      layoutChart();
      if (now - start < ms) frames.add(window.requestAnimationFrame(step));
    };
    frames.add(window.requestAnimationFrame(step));
  }
  layoutChart();
  return {
    lines,
    setReveal,
    relayoutFor,
    hit,
    seal
  };
}

// src/confetti.ts
var CONFETTI_COLORS = ["#82d675", "#ff8871", "#edab00", "#fafafa", "#7a9fed"];
var CONFETTI_BURST = {
  beat: {
    count: 140,
    spread: 120,
    kick: 26
  },
  win: {
    count: 360,
    spread: 420,
    kick: 40
  }
};
function confetti(canvas, frames, isDestroyed, kind = "beat") {
  const ctx = canvas.getContext("2d");
  if (ctx === null) return;
  const burst = CONFETTI_BURST[kind];
  const W = canvas.offsetWidth * 2;
  const H = canvas.offsetHeight * 2;
  canvas.width = W;
  canvas.height = H;
  const parts = Array.from({
    length: burst.count
  }, () => ({
    x: W / 2 + (Math.random() - 0.5) * burst.spread,
    y: H * 0.55,
    vx: (Math.random() - 0.5) * burst.kick,
    vy: -Math.random() * 30 - 8,
    w: 6 + Math.random() * 8,
    h: 4 + Math.random() * 5,
    rot: Math.random() * Math.PI,
    vr: (Math.random() - 0.5) * 0.3,
    c: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)] ?? "#fafafa"
  }));
  const start = performance.now();
  const frame = now => {
    if (isDestroyed()) return;
    const t = (now - start) / 1e3;
    ctx.clearRect(0, 0, W, H);
    ctx.globalAlpha = Math.max(0, 1 - Math.max(0, t - 1.2) / 0.7);
    for (const p of parts) {
      p.vy += 1.1;
      p.x += p.vx;
      p.y += p.vy;
      p.vx *= 0.985;
      p.rot += p.vr;
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.fillStyle = p.c;
      ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
      ctx.restore();
    }
    if (t < 2 && canvas.isConnected) frames.add(window.requestAnimationFrame(frame));else ctx.clearRect(0, 0, W, H);
  };
  frames.add(window.requestAnimationFrame(frame));
}

// src/simulated-trade-runner.ts
var LAST_STAGE = 6;
var BUBBLE_COUNT = 4;
var METER_COLS = 80;
function createSimulatedTradeRunner(root, opts) {
  const {
    isStatic,
    format
  } = opts;
  const L = () => opts.labels();
  const q = key => {
    const el = root.querySelector(`[data-stf="${key}"]`);
    if (el === null) throw new Error(`simulated-trade: missing [data-stf="${key}"]`);
    return el;
  };
  const qa = key => Array.from(root.querySelectorAll(`[data-stf="${key}"]`));
  const stageEl = q("stage");
  const card = q("card");
  const inter = q("inter");
  const interText = q("inter-text");
  const snack = q("snack");
  const snackText = q("snack-text");
  const cursor = q("cursor");
  const confettiCanvas = q("confetti");
  let stage = 0;
  let stageComplete = false;
  let fastForward = false;
  let base = 0;
  let destroyed = false;
  const jumping = () => isStatic || fastForward;
  const timers = /* @__PURE__ */new Set();
  const frames = /* @__PURE__ */new Set();
  function at(ms, fn) {
    if (jumping()) {
      fn();
      return;
    }
    const id = window.setTimeout(() => {
      timers.delete(id);
      if (!destroyed) fn();
    }, ms + base);
    timers.add(id);
  }
  function tween(from, to, ms, onUpdate, ease = easeOutCubic) {
    if (jumping()) {
      onUpdate(to);
      return;
    }
    const start2 = performance.now();
    const step = now => {
      if (destroyed) return;
      const p = Math.min(1, (now - start2) / ms);
      onUpdate(from + (to - from) * ease(p));
      if (p < 1) frames.add(window.requestAnimationFrame(step));
    };
    frames.add(window.requestAnimationFrame(step));
  }
  function clearAll() {
    for (const id of timers) window.clearTimeout(id);
    timers.clear();
    for (const id of frames) window.cancelAnimationFrame(id);
    frames.clear();
    confettiCanvas.getContext("2d")?.clearRect(0, 0, confettiCanvas.width, confettiCanvas.height);
  }
  function endInterstitial(text) {
    if (jumping() || text === null) return;
    interText.textContent = text;
    inter.classList.add("is-in");
  }
  function showSnack(text, ms) {
    if (jumping()) return;
    snackText.textContent = text;
    snack.classList.add("is-in");
    at(ms, () => snack.classList.remove("is-in"));
  }
  function cursorTo(el) {
    let x = 0;
    let y = 0;
    let node = el;
    while (node !== null && node !== stageEl) {
      x += node.offsetLeft;
      y += node.offsetTop;
      node = node.offsetParent instanceof HTMLElement ? node.offsetParent : null;
    }
    cursor.style.left = `${x + el.offsetWidth * 0.55}px`;
    cursor.style.top = `${y + el.offsetHeight * 0.62}px`;
  }
  function click(btn, t) {
    at(t, () => {
      if (jumping()) return;
      cursor.classList.add("is-in");
      cursorTo(btn);
    });
    at(t + 650, () => {
      btn.classList.add("is-pressed");
      if (!jumping()) cursor.classList.add("is-press");
    });
    at(t + 850, () => cursor.classList.remove("is-press"));
  }
  const meterDots = qa("meter-dot");
  const fundedEl = q("funded");
  function setFunded(amount) {
    const cols = Math.round(amount / NUMBERS.fundingGoal * METER_COLS);
    for (const dot of meterDots) {
      dot.classList.toggle("on", Number(dot.dataset.col) < cols);
    }
    fundedEl.textContent = format.money(amount);
  }
  const {
    lines,
    setReveal,
    relayoutFor,
    hit,
    seal
  } = createChart(q, qa, jumping, () => destroyed, frames);
  const pctYes = q("pct-yes");
  const pctNo = q("pct-no");
  const tileYes = q("tile-yes");
  const tileNo = q("tile-no");
  function setShare(yes) {
    pctYes.textContent = `${Math.round(yes)}%`;
    pctNo.textContent = `${Math.round(100 - yes)}%`;
    tileYes.classList.toggle("is-leading", yes > 50);
    tileNo.classList.toggle("is-leading", yes < 50);
  }
  const posVal = q("pos-val");
  const posProfit = q("pos-profit");
  function setPosition(value) {
    posVal.textContent = format.money(value);
    posProfit.textContent = format.money(Math.max(0, value - NUMBERS.positionCost));
  }
  function setButton(mode, n) {
    const meta = STAGE_BUTTON[n];
    const text = L().stages[n];
    if (fastForward) return;
    opts.onButton({
      label: mode === "during" ? text.status : text.action,
      disabled: mode === "during",
      spinner: mode === "during" && meta.spinner
    });
  }
  const tradesEl = q("trades");
  function spawnTrade(label, side, i, total) {
    if (fastForward) return;
    if (isStatic && i < total - 5) return;
    const el = document.createElement("span");
    el.className = `stf-trade stf-trade--${side}${isStatic ? " is-static" : ""}`;
    el.textContent = label;
    if (isStatic) {
      const k = total - 1 - i;
      el.style.setProperty("--y", `${-k * 24}px`);
      el.style.setProperty("--o", `${1 - k * 0.18}`);
    }
    tradesEl.appendChild(el);
    if (!isStatic) at(TRADE_LIFE_MS, () => el.remove());
  }
  const tag = q("tag");
  const tagText = q("tag-text");
  function flipTag(text, cls) {
    tag.classList.add("is-flipping");
    at(180, () => {
      tagText.textContent = text;
      tag.classList.remove("is-live", "is-resolved", "is-flipping");
      tag.classList.add(cls);
    });
  }
  const timelines = {
    1() {
      stageEl.dataset.stage = "1";
      for (const i of [0, 1, 2]) at(250 + i * 900, () => q(`bubble-${i}`).classList.add("is-in"));
      at(3e3, () => {
        card.classList.remove("is-hidden");
        q("bubble-3").classList.add("is-in");
      });
      return getStageDurationMs(1);
    },
    2() {
      stageEl.dataset.stage = "2";
      card.classList.remove("is-hidden");
      q("row-tag").classList.add("is-open");
      q("row-outcomes").classList.add("is-open");
      q("row-meter").classList.add("is-open");
      qa("chip").forEach((chip, i) => {
        const t = 950 + i * 850;
        at(t, () => chip.classList.add("is-in"));
        at(t + 320, () => tween(i * NUMBERS.fundingStep, (i + 1) * NUMBERS.fundingStep, 420, setFunded));
      });
      const liveAt = 950 + 3 * 850 + 320 + 520;
      at(liveAt, () => {
        q("goal").classList.add("is-hit");
        flipTag(L().tagLive, "is-live");
        card.classList.add("is-live");
        if (!jumping()) confetti(confettiCanvas, frames, () => destroyed);
      });
      return getStageDurationMs(2);
    },
    3() {
      stageEl.dataset.stage = "3";
      q("row-meter").classList.remove("is-open");
      q("row-chart").classList.add("is-open");
      q("pool").classList.add("is-in");
      relayoutFor(CHART_RELAYOUT_MS);
      const t0 = 700;
      const ms = CHART.stage3Ms;
      at(t0, () => {
        tween(0, 1, ms, f => {
          for (const k of ["no", "yes"]) setReveal(k, lines[k].split * f);
          setShare(yesShareAt(f * CHART.splitIndex));
        }, easeInOutCubic);
        opts.onPool(NUMBERS.prizePool);
      });
      L().trades3.forEach(([t, label, side], i) => {
        at(t0 + t, () => spawnTrade(label, side, i, L().trades3.length));
      });
      const buyAt = t0 + 2950;
      at(buyAt, () => {
        showSnack(L().snackBought, 2600);
        if (!jumping()) confetti(confettiCanvas, frames, () => destroyed);
        stageEl.classList.add("has-position");
        q("row-position").classList.add("is-open");
        q("pos").classList.add("is-in");
        relayoutFor(CHART_RELAYOUT_MS);
        tween(NUMBERS.positionCost, NUMBERS.positionStage3, t0 + ms - buyAt + 200, setPosition);
      });
      return getStageDurationMs(3);
    },
    4() {
      stageEl.dataset.stage = "4";
      tradesEl.replaceChildren();
      const t0 = 300;
      const ms = CHART.stage4Ms;
      at(t0, () => {
        tween(0, 1, ms, f => {
          for (const k of ["no", "yes"]) {
            setReveal(k, lines[k].split + (1 - lines[k].split) * f);
          }
          setShare(yesShareAt(CHART.splitIndex + f * (15 - CHART.splitIndex)));
        }, easeInOutCubic);
        tween(NUMBERS.positionStage3, NUMBERS.positionStage4, ms, setPosition, easeInOutCubic);
      });
      L().trades4.forEach(([t, label, side], i) => {
        at(t0 + t, () => spawnTrade(label, side, i, L().trades4.length));
      });
      at(t0 + ms, () => {
        hit.classList.add("is-in");
        q("chart-wrap").classList.add("losers-dimmed");
        flipTag(L().tagResolved, "is-resolved");
        showSnack(L().snackWon, 3e3);
        if (!jumping()) confetti(confettiCanvas, frames, () => destroyed, "win");
        tileYes.classList.add("is-winner");
        tileNo.classList.add("is-loser");
      });
      at(t0 + ms + 350, () => {
        lines.yes.tip.classList.add("is-off");
        seal.classList.add("is-in");
      });
      return getStageDurationMs(4);
    },
    5() {
      stageEl.dataset.stage = "5";
      q("row-chart").classList.remove("is-open");
      q("row-claim-pool").classList.add("is-open");
      q("row-claim-backer").classList.add("is-open");
      q("pool").classList.remove("is-in");
      at(500, () => {
        q("pos-hold").classList.add("is-hidden");
        q("pos-sell").classList.remove("is-hidden");
      });
      const sellBtn = q("sell-btn");
      const holdVal = q("hold-val");
      const cashBox = q("cash-box");
      const cash = q("cash");
      click(sellBtn, 900);
      at(1750, () => {
        cashBox.classList.add("is-in");
        tween(NUMBERS.sold, 0, 1e3, v => {
          holdVal.textContent = format.money(v);
        }, linear);
        tween(0, NUMBERS.sold, 1e3, v => {
          cash.textContent = format.money(v);
        });
        showSnack(L().snackSold, 2400);
      });
      at(2850, () => {
        sellBtn.classList.remove("is-pressed");
        sellBtn.classList.add("is-done");
        sellBtn.textContent = L().sold;
      });
      at(3e3, () => q("claim-pool").classList.add("is-in"));
      const poolBtn = q("pool-btn");
      click(poolBtn, 3300);
      at(4150, () => {
        q("pool-cash-box").classList.add("is-in");
        const el = q("pool-cash");
        tween(0, NUMBERS.poolShare, 800, v => {
          el.textContent = format.money(v);
        });
      });
      at(5e3, () => {
        poolBtn.classList.remove("is-pressed");
        poolBtn.classList.add("is-done");
        poolBtn.textContent = L().claimed;
      });
      at(5200, () => q("claim-backer").classList.add("is-in"));
      const claimBtn = q("claim-btn");
      click(claimBtn, 5500);
      at(6350, () => {
        q("reward-box").classList.add("is-in");
        const el = q("reward");
        tween(0, NUMBERS.backerRewards, 700, v => {
          el.textContent = format.money(v);
        });
      });
      at(7100, () => {
        claimBtn.classList.remove("is-pressed");
        claimBtn.classList.add("is-done");
        claimBtn.textContent = L().claimed;
      });
      at(7400, () => cursor.classList.remove("is-in"));
      return getStageDurationMs(5);
    },
    6() {
      stageEl.dataset.stage = "6";
      q("row-outcomes").classList.remove("is-open");
      q("row-position").classList.remove("is-open");
      q("row-claim-pool").classList.remove("is-open");
      q("row-claim-backer").classList.remove("is-open");
      q("row-receipt").classList.add("is-open");
      tagText.textContent = L().tagResolvedShort;
      const rows = qa("rc-row");
      const profitEl = q("profit");
      rows.forEach((row, i) => {
        at(600 + i * 160, () => {
          row.classList.add("is-in");
          if (i === rows.length - 2) {
            tween(0, NUMBERS.profit, 900, v => {
              profitEl.textContent = format.money(v);
            });
          }
        });
      });
      at(600 + 7 * 160 - 120, () => q("rc-div").classList.add("is-in"));
      return getStageDurationMs(6);
    }
  };
  function goTo(n) {
    clearAll();
    stage = n;
    stageComplete = false;
    setButton("during", n);
    const wasCovered = inter.classList.contains("is-in");
    inter.classList.remove("is-in");
    base = wasCovered && !jumping() ? SCRIM_FADE_OUT_MS : 0;
    const doneAt = timelines[n]();
    at(doneAt, () => {
      stageComplete = true;
      setButton("done", n);
      endInterstitial(STAGE_BUTTON[n].endLine ? L().stages[n].endLine : null);
    });
    base = 0;
    if (!fastForward) opts.onStage(n);
  }
  function setIntroPreview(on) {
    stageEl.classList.add("no-anim");
    stageEl.dataset.stage = "1";
    for (let i = 0; i < BUBBLE_COUNT; i += 1) q(`bubble-${i}`).classList.toggle("is-in", on);
    card.classList.toggle("is-hidden", !on);
    void stageEl.offsetHeight;
    stageEl.classList.remove("no-anim");
  }
  function intro() {
    clearAll();
    stage = 0;
    setIntroPreview(true);
    opts.onButton({
      label: L().introButton,
      disabled: false,
      spinner: false
    });
    endInterstitial(L().introLine);
    opts.onStage(0);
  }
  function nextStage(n) {
    return Math.max(1, Math.min(LAST_STAGE, n));
  }
  function next() {
    if (stage >= LAST_STAGE) {
      opts.onClose();
      return;
    }
    if (stage === 0) setIntroPreview(false);
    if (stage > 0 && !stageComplete) {
      fastForward = true;
      goTo(nextStage(stage));
      fastForward = false;
    }
    goTo(nextStage(stage + 1));
  }
  function start() {
    if (opts.initialStage > 0) {
      fastForward = true;
      for (let n = 1; n < opts.initialStage; n += 1) goTo(nextStage(n));
      fastForward = false;
      goTo(nextStage(opts.initialStage));
      return;
    }
    if (isStatic) {
      goTo(1);
      return;
    }
    intro();
  }
  function destroy() {
    destroyed = true;
    clearAll();
  }
  return {
    start,
    next,
    destroy
  };
}

// src/animated-number.tsx
import { useLayoutEffect, useRef } from "react";
import { jsx } from "react/jsx-runtime";
function AnimatedNumber({
  value,
  format,
  duration,
  className,
  isStatic
}) {
  const span = useRef(null);
  const current = useRef(value);
  useLayoutEffect(() => {
    const node = span.current;
    if (node === null) return;
    const from = current.current;
    if (from === value || isStatic || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      current.current = value;
      node.textContent = format(value);
      return;
    }
    let frame = 0;
    const started = performance.now();
    const tick = () => {
      const fraction = Math.min((performance.now() - started) / duration, 1);
      current.current = from + (value - from) * (1 - (1 - fraction) ** 3);
      node.textContent = format(current.current);
      if (fraction < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [value, format, duration, isStatic]);
  return /* @__PURE__ */jsx("span", {
    ref: span,
    className
  });
}

// src/glyphs.ts
var Award01IconSolid = [["path", {
  d: "M17.0117 1.25C17.9641 1.25038 18.7667 2.01881 18.748 3H20.5097C21.5968 3 22.5378 3.88348 22.499 5.03223C22.4546 6.34261 22.2669 8.30281 21.415 10.1318C20.5488 11.9911 18.9938 13.7083 16.2997 14.4531C16.0061 15.0291 15.6811 15.5576 15.3144 16.0127C14.8708 16.563 14.3522 17.0244 13.7587 17.334C13.8585 19.0095 14.7114 20.6548 15.5263 21.4697C15.7408 21.6842 15.8054 22.0069 15.6894 22.2871C15.5733 22.5673 15.2993 22.75 14.996 22.75H8.99603C8.69281 22.7499 8.41872 22.5673 8.30267 22.2871C8.18672 22.007 8.25144 21.6842 8.46576 21.4697C9.27533 20.6602 10.1218 19.0303 10.2363 17.3643C9.62646 17.0546 9.09797 16.5789 8.6474 16.0127C8.28337 15.5552 7.96018 15.0234 7.66889 14.4434C4.99439 13.6938 3.44764 11.9838 2.5849 10.1318C1.73297 8.30283 1.54523 6.34259 1.50092 5.03223C1.46209 3.88354 2.4032 3.0001 3.49017 3H5.25189C5.2333 2.01881 6.03551 1.25025 6.98822 1.25H17.0117ZM18.6103 5C18.4062 7.18732 18.0012 9.68546 17.3232 11.8721C18.4235 11.2184 19.1322 10.2953 19.6015 9.28809C20.2773 7.83745 20.4546 6.21377 20.498 5H18.6103ZM3.50189 5C3.54526 6.21375 3.72263 7.83747 4.39838 9.28809C4.8653 10.2902 5.56938 11.2092 6.6601 11.8623C5.99172 9.68018 5.58998 7.1876 5.38764 5H3.50189Z",
  fill: "currentColor",
  key: "0"
}]];
var CheckmarkCircle02IconSolid = [["path", {
  d: "M11.75 22.5C5.81294 22.5 1 17.6871 1 11.75C1 5.81294 5.81294 1 11.75 1C17.6871 1 22.5 5.81294 22.5 11.75C22.5 17.6871 17.6871 22.5 11.75 22.5ZM16.5182 9.39018C16.8718 8.9659 16.8145 8.33534 16.3902 7.98177C15.9659 7.62821 15.3353 7.68553 14.9818 8.10981L10.6828 13.2686L8.45711 11.0429C8.06658 10.6524 7.43342 10.6524 7.04289 11.0429C6.65237 11.4334 6.65237 12.0666 7.04289 12.4571L10.0429 15.4571C10.2416 15.6558 10.5146 15.7617 10.7953 15.749C11.076 15.7362 11.3384 15.606 11.5182 15.3902L16.5182 9.39018Z",
  fill: "currentColor",
  fillRule: "evenodd",
  clipRule: "evenodd",
  key: "0"
}]];
var CircleArrowDown02IconSolid = [["path", {
  d: "M12 1.25C17.9371 1.25 22.75 6.06294 22.75 12C22.75 17.9371 17.9371 22.75 12 22.75C6.06294 22.75 1.25 17.9371 1.25 12C1.25 6.06294 6.06294 1.25 12 1.25ZM12 7C11.4477 7 11 7.44772 11 8V13.5859L9.20703 11.793C8.81651 11.4024 8.18349 11.4025 7.79297 11.793C7.40244 12.1835 7.40244 12.8165 7.79297 13.207L11.293 16.707L11.3691 16.7754C11.7619 17.0957 12.3409 17.0731 12.707 16.707L16.207 13.207L16.2754 13.1309C16.5957 12.7381 16.5731 12.1591 16.207 11.793C15.8409 11.4269 15.2619 11.4043 14.8691 11.7246L14.793 11.793L13 13.5859V8C13 7.44772 12.5523 7.00002 12 7Z",
  fill: "currentColor",
  key: "0"
}]];
var Coins01IconSolid = [["path", {
  d: "M22 14.2078V11C22 10.2861 21.5651 9.7652 21.1475 9.43611C20.7186 9.09812 20.16 8.83749 19.5589 8.63711C18.3462 8.23287 16.7344 8.00001 15 8.00001C13.2656 8.00001 11.6538 8.23287 10.4411 8.63711C9.84 8.83749 9.2814 9.09812 8.85254 9.43611C8.43495 9.7652 8 10.2861 8 11V14.1699C8.0482 14.3288 8.17306 14.5159 8.44117 14.7298C8.81278 15.0262 9.37972 15.3041 10.1022 15.54C11.5405 16.0095 13.4029 16.2513 14.9997 16.2513C16.5971 16.2513 18.463 16.0116 19.9045 15.5431C20.6287 15.3077 21.197 15.0301 21.5695 14.7333C21.8182 14.5353 21.9442 14.3598 22 14.2078ZM11.0736 11.4655C10.5969 11.3066 10.2794 11.1392 10.0994 11C10.2794 10.8608 10.5969 10.6934 11.0736 10.5345C12.0325 10.2149 13.4207 10 15 10C16.5793 10 17.9675 10.2149 18.9264 10.5345C19.4031 10.6934 19.7206 10.8608 19.9006 11C19.7206 11.1392 19.4031 11.3066 18.9264 11.4655C17.9675 11.7852 16.5793 12 15 12C13.4207 12 12.0325 11.7852 11.0736 11.4655Z",
  fill: "currentColor",
  fillRule: "evenodd",
  clipRule: "evenodd",
  key: "0"
}], ["path", {
  d: "M22 16.2537C21.5182 16.5428 20.9568 16.7783 20.3682 16.9696C18.7441 17.4974 16.716 17.7513 14.9997 17.7513C13.2827 17.7513 11.2575 17.495 9.63673 16.9659C9.04579 16.773 8.48247 16.5355 8 16.2441V19C8 19.7139 8.43495 20.2348 8.85254 20.5639C9.2814 20.9019 9.84 21.1625 10.4411 21.3629C11.6538 21.7671 13.2656 22 15 22C16.7344 22 18.3462 21.7671 19.5589 21.3629C20.16 21.1625 20.7186 20.9019 21.1475 20.5639C21.5651 20.2348 22 19.7139 22 19V16.2537Z",
  fill: "currentColor",
  key: "1"
}], ["path", {
  d: "M15 6.50001V4.00001C15 3.28613 14.5651 2.7652 14.1475 2.43611C13.7186 2.09812 13.16 1.83749 12.5589 1.63711C11.3462 1.23287 9.73443 1.00001 8 1.00001C6.26557 1.00001 4.65384 1.23287 3.44113 1.63711C2.84 1.83749 2.2814 2.09812 1.85254 2.43611C1.43495 2.7652 1 3.28613 1 4.00001V7.50268C1.03948 7.56549 1.13378 7.68316 1.3587 7.84888C1.71242 8.1095 2.24339 8.37751 2.89196 8.61955C4.16887 9.0961 5.7367 9.41189 6.91313 9.42174C7.20682 8.89685 7.60428 8.51002 7.92408 8.25799C8.54071 7.77202 9.27813 7.44364 9.96679 7.21409C11.3699 6.74639 13.1492 6.50001 15 6.50001ZM4.07359 4.46554C3.59686 4.30663 3.27937 4.13923 3.09935 4.00001C3.27937 3.86079 3.59686 3.69339 4.07359 3.53448C5.03245 3.21486 6.42072 3.00001 8 3.00001C9.57928 3.00001 10.9675 3.21486 11.9264 3.53448C12.4031 3.69339 12.7206 3.86079 12.9006 4.00001C12.7206 4.13923 12.4031 4.30663 11.9264 4.46554C10.9675 4.78516 9.57928 5.00001 8 5.00001C6.42072 5.00001 5.03245 4.78516 4.07359 4.46554Z",
  fill: "currentColor",
  fillRule: "evenodd",
  clipRule: "evenodd",
  key: "2"
}], ["path", {
  d: "M1 12.2952V9.39884C1.4101 9.63278 1.8787 9.84246 2.36749 10.0249C3.64395 10.5013 5.19318 10.8409 6.50123 10.9093C6.50041 10.9393 6.5 10.9695 6.5 11V13.9196C5.37069 13.8591 3.98946 13.6045 2.84195 13.2273C2.1797 13.0095 1.63929 12.7651 1.28042 12.5221C1.14328 12.4293 1.05576 12.353 1 12.2952Z",
  fill: "currentColor",
  key: "3"
}], ["path", {
  d: "M1 14.0925V16C1 16.7139 1.43495 17.2348 1.85254 17.5639C2.2814 17.9019 2.84 18.1625 3.44113 18.3629C4.29506 18.6475 5.34685 18.8472 6.5 18.9405V15.4214C5.20769 15.3609 3.66002 15.0752 2.37348 14.6522C1.88511 14.4917 1.41446 14.305 1 14.0925Z",
  fill: "currentColor",
  key: "4"
}]];
var Touch01IconSolid = [["path", {
  d: "M8.25 3.75C8.25 2.36929 9.36929 1.25 10.75 1.25C12.1307 1.25 13.25 2.36929 13.25 3.75V8.74751C15.1139 8.81064 17.819 9.38616 19.8531 11.4332C20.789 12.3751 20.8445 13.7529 20.6737 14.8931C20.4986 16.0631 20.0522 17.2226 19.6813 17.9998L18.75 20.1551V22C18.75 22.4142 18.4142 22.75 18 22.75H9C8.58579 22.75 8.25 22.4142 8.25 22V20.2558L3.78831 14.5527L3.78749 14.5517C3.07481 13.6459 3.07046 12.3565 3.77682 11.4457C4.63524 10.3388 6.22827 10.1793 7.2802 11.1094C7.29632 11.1236 7.31182 11.1386 7.32666 11.1542L8.25 12.1242V3.75Z",
  fill: "currentColor",
  fillRule: "evenodd",
  clipRule: "evenodd",
  key: "0"
}]];
var TradeUpIconSolid = [["path", {
  d: "M14 8C14 7.44772 14.4477 7 15 7H20C20.5523 7 21 7.44772 21 8V13C21 13.5523 20.5523 14 20 14C19.4477 14 19 13.5523 19 13V9H15C14.4477 9 14 8.55228 14 8Z",
  fill: "currentColor",
  fillRule: "evenodd",
  clipRule: "evenodd",
  key: "0"
}], ["path", {
  d: "M20.7071 7.29289C21.0976 7.68342 21.0976 8.31658 20.7071 8.70711L13.7071 15.7071C13.3166 16.0976 12.6834 16.0976 12.2929 15.7071L9 12.4142L4.70711 16.7071C4.31658 17.0976 3.68342 17.0976 3.29289 16.7071C2.90237 16.3166 2.90237 15.6834 3.29289 15.2929L8.29289 10.2929C8.68342 9.90237 9.31658 9.90237 9.70711 10.2929L13 13.5858L19.2929 7.29289C19.6834 6.90237 20.3166 6.90237 20.7071 7.29289Z",
  fill: "currentColor",
  fillRule: "evenodd",
  clipRule: "evenodd",
  key: "1"
}]];
var ArrowUp01Icon = [["path", {
  d: "M18 15L12 9L6 15",
  stroke: "currentColor",
  strokeLinecap: "round",
  strokeLinejoin: "round",
  strokeWidth: "1.5",
  key: "0"
}]];

// src/icon.tsx
import { createElement } from "react";
import { jsx as jsx2 } from "react/jsx-runtime";
function Icon({
  icon,
  ...props
}) {
  const stroke = icon.some(([, attributes]) => attributes.stroke !== void 0) ? {
    stroke: "currentColor",
    strokeWidth: 2
  } : {};
  return /* @__PURE__ */jsx2("svg", {
    viewBox: "0 0 24 24",
    width: "24",
    height: "24",
    fill: "none",
    "aria-hidden": "true",
    ...props,
    children: icon.map(([tag, attributes]) => createElement(tag, {
      ...attributes,
      ...stroke
    }))
  });
}

// src/image.tsx
import { jsx as jsx3 } from "react/jsx-runtime";
function Image(props) {
  return /* @__PURE__ */jsx3("img", {
    alt: "",
    ...props
  });
}

// src/stage-chart.tsx
import { jsx as jsx4, jsxs } from "react/jsx-runtime";
function StageChart() {
  const $ = _c(2);
  const {
    t
  } = usePresentation();
  let t0;
  if ($[0] !== t) {
    t0 = jsx4("div", {
      className: "stf-row stf-row--chart",
      "data-stf": "row-chart",
      children: jsxs("div", {
        className: "stf-chart-wrap",
        "data-stf": "chart-wrap",
        children: [jsx4("div", {
          className: "stf-chart-grid"
        }), jsxs("svg", {
          className: "stf-chart",
          viewBox: `0 0 ${CHART.viewWidth} 160`,
          preserveAspectRatio: "none",
          "data-stf": "chart",
          "aria-hidden": "true",
          children: [CHART.axisTicks.map(_temp), jsx4("line", {
            className: "stf-target",
            x1: "0",
            x2: CHART.viewWidth,
            "data-stf": "target"
          }), jsx4("path", {
            className: "stf-line stf-line--no",
            "data-stf": "line-no"
          }), jsx4("path", {
            className: "stf-line stf-line--yes",
            "data-stf": "line-yes"
          })]
        }), jsxs("div", {
          className: "stf-chart-ui",
          children: [CHART.axisTicks.map(_temp2), jsxs("span", {
            className: "stf-badge",
            "data-stf": "badge",
            children: [jsx4(Icon, {
              icon: ArrowUp01Icon,
              className: "size-icon-2xs",
              "aria-hidden": "true"
            }), t("chart_target", {
              pct: CHART.target
            })]
          }), jsx4("span", {
            className: "stf-tip stf-tip--no",
            "data-stf": "tip-no"
          }), jsx4("span", {
            className: "stf-tip",
            "data-stf": "tip-yes"
          }), jsx4("span", {
            className: "stf-hit",
            "data-stf": "hit"
          }), jsx4("span", {
            className: "stf-seal",
            "data-stf": "seal",
            children: jsx4(Icon, {
              icon: CheckmarkCircle02IconSolid,
              className: "size-icon-xl",
              "aria-hidden": "true"
            })
          })]
        }), jsx4("div", {
          className: "stf-trades",
          "data-stf": "trades"
        })]
      })
    });
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}

// src/stage-shared.tsx
function _temp2(tick_0) {
  return jsxs("span", {
    className: "stf-axis-label",
    "data-stf": "axis-label",
    children: [tick_0, "%"]
  }, tick_0);
}
function _temp(tick) {
  return jsx4("line", {
    className: "stf-grid-line",
    x1: "0",
    x2: "452",
    "data-stf": "grid-line"
  }, tick);
}
import { jsx as jsx5 } from "react/jsx-runtime";
function PositionThumb() {
  const $ = _c(2);
  const {
    assets
  } = usePresentation();
  let t0;
  if ($[0] !== assets.outcomes.yes) {
    t0 = jsx5("div", {
      className: "stf-pos-thumb",
      children: jsx5(Image, {
        src: assets.outcomes.yes,
        alt: "",
        width: 40,
        height: 40
      })
    });
    $[0] = assets.outcomes.yes;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function UpGlyph() {
  return /* @__PURE__ */jsx5(Icon, {
    icon: CircleArrowDown02IconSolid,
    className: "size-icon-md rotate-180",
    "aria-hidden": "true"
  });
}

// src/stage-position.tsx
import { Fragment, jsx as jsx6, jsxs as jsxs2 } from "react/jsx-runtime";
function StagePosition(t0) {
  const $ = _c(3);
  const {
    format
  } = t0;
  const {
    t
  } = usePresentation();
  let t1;
  if ($[0] !== format || $[1] !== t) {
    t1 = jsx6("div", {
      className: "stf-row stf-row--position",
      "data-stf": "row-position",
      children: jsxs2("div", {
        className: "stf-pos",
        "data-stf": "pos",
        children: [jsxs2("div", {
          className: "stf-pos-head",
          children: [jsx6(Icon, {
            icon: TradeUpIconSolid,
            className: "size-icon-sm",
            "aria-hidden": "true"
          }), t("position_label"), jsx6("i", {
            className: "stf-live-dot"
          })]
        }), jsxs2("div", {
          className: "stf-pos-body",
          children: [jsxs2("div", {
            className: "stf-pos-mode",
            "data-stf": "pos-hold",
            children: [jsx6(PositionThumb, {}), jsxs2("div", {
              className: "stf-pos-stat",
              children: [jsx6("span", {
                className: "stf-pos-cap",
                children: t("position_current_value")
              }), jsx6("span", {
                className: "stf-pos-val",
                "data-stf": "pos-val",
                children: format.money(NUMBERS.positionCost)
              })]
            }), jsxs2("div", {
              className: "stf-pos-stat stf-pos-stat--right",
              children: [jsx6("span", {
                className: "stf-pos-cap",
                children: t("position_profit")
              }), jsxs2("span", {
                className: "stf-pos-val stf-pos-val--profit",
                children: [jsx6(UpGlyph, {}), jsx6("span", {
                  "data-stf": "pos-profit",
                  children: format.money(0)
                })]
              })]
            })]
          }), jsxs2("div", {
            className: "stf-pos-mode is-hidden",
            "data-stf": "pos-sell",
            children: [jsx6(PositionThumb, {}), jsxs2("div", {
              className: "stf-pos-stat",
              children: [jsx6("span", {
                className: "stf-pos-cap",
                children: t("position_holdings")
              }), jsx6("span", {
                className: "stf-pos-val",
                "data-stf": "hold-val",
                children: format.money(NUMBERS.sold)
              })]
            }), jsxs2("div", {
              className: "stf-pos-stat stf-pos-stat--right stf-pos-stat--row",
              children: [jsxs2("span", {
                className: "stf-cash",
                "data-stf": "cash-box",
                children: [jsx6(UpGlyph, {}), jsx6("b", {
                  "data-stf": "cash",
                  children: format.money(0)
                })]
              }), jsx6("span", {
                className: "stf-mini-btn",
                "data-stf": "sell-btn",
                children: t("position_sell")
              })]
            })]
          })]
        })]
      })
    });
    $[0] = format;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function StageClaims(t0) {
  const $ = _c(3);
  const {
    format
  } = t0;
  const {
    t
  } = usePresentation();
  let t1;
  if ($[0] !== format || $[1] !== t) {
    t1 = jsxs2(Fragment, {
      children: [jsx6("div", {
        className: "stf-row stf-row--claim",
        "data-stf": "row-claim-pool",
        children: jsxs2("div", {
          className: "stf-claim",
          "data-stf": "claim-pool",
          children: [jsx6(Icon, {
            icon: Award01IconSolid,
            className: "stf-claim-glyph size-icon-md",
            "aria-hidden": "true"
          }), jsxs2("div", {
            children: [jsx6("span", {
              className: "stf-claim-label",
              children: t("claim_pool_label")
            }), jsx6("span", {
              className: "stf-claim-sub",
              children: t("claim_pool_sub", {
                pool: format.compact(NUMBERS.prizePool)
              })
            })]
          }), jsxs2("span", {
            className: "stf-cash",
            "data-stf": "pool-cash-box",
            children: [jsx6(UpGlyph, {}), jsx6("b", {
              "data-stf": "pool-cash",
              children: format.money(0)
            })]
          }), jsx6("span", {
            className: "stf-mini-btn",
            "data-stf": "pool-btn",
            children: t("claim")
          })]
        })
      }), jsx6("div", {
        className: "stf-row stf-row--claim",
        "data-stf": "row-claim-backer",
        children: jsxs2("div", {
          className: "stf-claim",
          "data-stf": "claim-backer",
          children: [jsx6(Icon, {
            icon: Coins01IconSolid,
            className: "stf-claim-glyph size-icon-md",
            "aria-hidden": "true"
          }), jsxs2("div", {
            children: [jsx6("span", {
              className: "stf-claim-label",
              children: t("claim_backer_label")
            }), jsx6("span", {
              className: "stf-claim-sub",
              children: t("claim_backer_sub")
            })]
          }), jsxs2("span", {
            className: "stf-cash",
            "data-stf": "reward-box",
            children: [jsx6(UpGlyph, {}), jsx6("b", {
              "data-stf": "reward",
              children: format.money(0)
            })]
          }), jsx6("span", {
            className: "stf-mini-btn",
            "data-stf": "claim-btn",
            children: t("claim")
          })]
        })
      })]
    });
    $[0] = format;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}

// src/stage-receipt.tsx
import { jsx as jsx7, jsxs as jsxs3 } from "react/jsx-runtime";
function StageReceipt(t0) {
  const $ = _c(3);
  const {
    format
  } = t0;
  const {
    t
  } = usePresentation();
  const {
    money
  } = format;
  let t1;
  if ($[0] !== money || $[1] !== t) {
    t1 = jsx7("div", {
      className: "stf-row stf-row--receipt",
      "data-stf": "row-receipt",
      children: jsxs3("div", {
        className: "stf-rc",
        children: [jsx7(ReceiptRow, {
          kind: "head",
          label: t("receipt_put_in"),
          value: money(NUMBERS.putIn)
        }), jsx7(ReceiptRow, {
          kind: "sub",
          icon: Coins01IconSolid,
          label: t("receipt_backed"),
          value: money(NUMBERS.fundingStep)
        }), jsx7(ReceiptRow, {
          kind: "sub",
          icon: TradeUpIconSolid,
          label: t("receipt_bought"),
          value: money(NUMBERS.positionCost)
        }), jsx7(ReceiptRow, {
          kind: "head",
          label: t("receipt_got_back"),
          value: money(NUMBERS.gotBack)
        }), jsx7(ReceiptRow, {
          kind: "sub",
          icon: TradeUpIconSolid,
          label: t("receipt_sold"),
          value: money(NUMBERS.sold)
        }), jsx7(ReceiptRow, {
          kind: "sub",
          icon: Award01IconSolid,
          label: t("receipt_pool"),
          value: money(NUMBERS.poolShare)
        }), jsx7(ReceiptRow, {
          kind: "sub",
          icon: Coins01IconSolid,
          label: t("receipt_backer"),
          value: money(NUMBERS.backerRewards)
        }), jsx7("div", {
          className: "stf-rc-div",
          "data-stf": "rc-div"
        }), jsxs3("div", {
          className: "stf-rc-row stf-rc-row--profit",
          "data-stf": "rc-row",
          children: [jsx7("span", {
            children: t("receipt_profit")
          }), jsxs3("b", {
            children: [jsx7(UpGlyph, {}), jsx7("span", {
              "data-stf": "profit",
              children: money(0)
            })]
          })]
        }), jsxs3("div", {
          className: "stf-rc-row stf-rc-row--mult",
          "data-stf": "rc-row",
          children: [jsx7("span", {}), jsx7("b", {
            children: t("receipt_multiple", {
              multiple: NUMBERS.multiple
            })
          })]
        })]
      })
    });
    $[0] = money;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function ReceiptRow({
  kind,
  icon,
  label,
  value
}) {
  return /* @__PURE__ */jsxs3("div", {
    className: `stf-rc-row stf-rc-row--${kind}`,
    "data-stf": "rc-row",
    children: [icon !== void 0 ? /* @__PURE__ */jsxs3("span", {
      className: "stf-rc-lbl",
      children: [/* @__PURE__ */jsx7(Icon, {
        icon,
        className: "stf-rc-lbl-glyph size-icon-sm",
        "aria-hidden": "true"
      }), label]
    }) : /* @__PURE__ */jsx7("span", {
      children: label
    }), /* @__PURE__ */jsx7("b", {
      children: value
    })]
  });
}

// src/stage-card.tsx
import { jsx as jsx8, jsxs as jsxs4 } from "react/jsx-runtime";
var METER_DOTS = Array.from({
  length: 80 * 3
}, (_, i) => i % 80);
function StageCard(t0) {
  const $ = _c(7);
  const {
    format,
    pool,
    isStatic
  } = t0;
  const {
    t,
    assets
  } = usePresentation();
  let t1;
  if ($[0] !== assets.outcomes.no || $[1] !== assets.outcomes.yes || $[2] !== format || $[3] !== isStatic || $[4] !== pool || $[5] !== t) {
    t1 = jsxs4("div", {
      className: "stf-card is-hidden",
      "data-stf": "card",
      children: [jsxs4("div", {
        className: "stf-pool",
        "data-stf": "pool",
        children: [jsxs4("div", {
          className: "stf-pool-label",
          children: [jsx8(Icon, {
            icon: Award01IconSolid,
            className: "size-icon-sm",
            "aria-hidden": "true"
          }), t("pool_label")]
        }), jsx8(AnimatedNumber, {
          className: "stf-pool-value",
          value: pool,
          format: format.compact,
          duration: CHART.stage3Ms,
          isStatic
        })]
      }), jsx8("div", {
        className: "stf-row stf-row--tag",
        "data-stf": "row-tag",
        children: jsxs4("span", {
          className: "stf-tag",
          "data-stf": "tag",
          children: [jsx8("i", {
            className: "stf-tag-dot"
          }), jsx8("span", {
            "data-stf": "tag-text",
            children: t("tag_proposal")
          })]
        })
      }), jsx8("div", {
        className: "stf-row stf-row--title is-open",
        "data-stf": "row-title",
        children: jsx8("p", {
          className: "stf-title",
          children: t("question")
        })
      }), jsx8("div", {
        className: "stf-row stf-row--outcomes",
        "data-stf": "row-outcomes",
        children: jsxs4("div", {
          className: "stf-tiles",
          children: [jsxs4("div", {
            className: "stf-tile stf-tile--yes",
            "data-stf": "tile-yes",
            children: [jsx8(Image, {
              className: "stf-tile-thumb",
              src: assets.outcomes.yes,
              alt: "",
              width: 40,
              height: 40
            }), jsx8("span", {
              className: "stf-tile-name",
              children: t("outcome_yes")
            }), jsx8("span", {
              className: "stf-tile-win",
              children: t("outcome_wins")
            }), jsx8("span", {
              className: "stf-tile-share",
              "data-stf": "pct-yes",
              children: "50%"
            })]
          }), jsxs4("div", {
            className: "stf-tile stf-tile--no",
            "data-stf": "tile-no",
            children: [jsx8(Image, {
              className: "stf-tile-thumb",
              src: assets.outcomes.no,
              alt: "",
              width: 40,
              height: 40
            }), jsx8("span", {
              className: "stf-tile-name",
              children: t("outcome_no")
            }), jsx8("span", {
              className: "stf-tile-share",
              "data-stf": "pct-no",
              children: "50%"
            })]
          })]
        })
      }), jsxs4("div", {
        className: "stf-row stf-row--meter",
        "data-stf": "row-meter",
        children: [jsxs4("div", {
          className: "stf-meter-head",
          children: [jsxs4("span", {
            children: [t("funding_label"), " ", jsx8("b", {
              "data-stf": "funded",
              children: format.money(0)
            })]
          }), jsxs4("span", {
            className: "stf-goal",
            "data-stf": "goal",
            children: [t("funding_goal"), " ", jsx8("b", {
              children: format.money(NUMBERS.fundingGoal)
            })]
          })]
        }), jsx8("div", {
          className: "stf-meter",
          children: METER_DOTS.map(_temp3)
        })]
      }), jsx8(StageChart, {}), jsx8(StagePosition, {
        format
      }), jsx8(StageClaims, {
        format
      }), jsx8(StageReceipt, {
        format
      })]
    });
    $[0] = assets.outcomes.no;
    $[1] = assets.outcomes.yes;
    $[2] = format;
    $[3] = isStatic;
    $[4] = pool;
    $[5] = t;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1;
}

// src/stage-chat.tsx
function _temp3(col, i) {
  return jsx8("i", {
    "data-stf": "meter-dot",
    "data-col": col
  }, `${col}-${Math.floor(i / 80)}`);
}
import { jsx as jsx9, jsxs as jsxs5 } from "react/jsx-runtime";
function StageChat() {
  const $ = _c(5);
  const {
    t,
    assets
  } = usePresentation();
  let t0;
  if ($[0] !== assets.avatars.dan || $[1] !== assets.avatars.priya || $[2] !== assets.avatars.you || $[3] !== t) {
    t0 = jsxs5("div", {
      className: "stf-bubbles",
      children: [jsx9(Bubble, {
        index: 0,
        side: "left",
        avatar: assets.avatars.dan,
        who: t("name_dan"),
        children: t("bubble_dan")
      }), jsx9(Bubble, {
        index: 1,
        side: "right",
        avatar: assets.avatars.you,
        who: t("name_you"),
        children: t("bubble_you")
      }), jsx9(Bubble, {
        index: 2,
        side: "left",
        avatar: assets.avatars.priya,
        who: t("name_priya"),
        children: t("bubble_priya")
      }), jsx9(Bubble, {
        index: 3,
        side: "right",
        avatar: assets.avatars.you,
        who: t("name_you")
      })]
    });
    $[0] = assets.avatars.dan;
    $[1] = assets.avatars.priya;
    $[2] = assets.avatars.you;
    $[3] = t;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  return t0;
}
function Bubble({
  index,
  side,
  avatar,
  who,
  children
}) {
  return /* @__PURE__ */jsxs5("div", {
    className: `stf-bubble stf-bubble--${side} stf-bubble--${index}`,
    "data-stf": `bubble-${index}`,
    children: [/* @__PURE__ */jsx9(Image, {
      className: "stf-av",
      src: avatar,
      alt: "",
      width: 28,
      height: 28
    }), children !== void 0 ? /* @__PURE__ */jsx9("span", {
      className: "stf-bubble-txt",
      children
    }) : null, /* @__PURE__ */jsx9("span", {
      className: "stf-bubble-who",
      children: who
    })]
  });
}

// src/stage-funding-chips.tsx
import { jsx as jsx10, jsxs as jsxs6 } from "react/jsx-runtime";
function StageFundingChips(t0) {
  const $ = _c(7);
  const {
    format
  } = t0;
  const {
    t,
    assets
  } = usePresentation();
  let t1;
  if ($[0] !== assets.avatars.dan || $[1] !== assets.avatars.maya || $[2] !== assets.avatars.priya || $[3] !== assets.avatars.you || $[4] !== format || $[5] !== t) {
    const backers = [["you", assets.avatars.you, t("name_you")], ["dan", assets.avatars.dan, t("name_dan")], ["priya", assets.avatars.priya, t("name_priya")], ["maya", assets.avatars.maya, t("name_maya")]];
    t1 = jsx10("div", {
      className: "stf-chips",
      children: backers.map((t2, i) => {
        const [key, avatar, name] = t2;
        return jsxs6("div", {
          className: `stf-chip stf-chip--${i}`,
          "data-stf": "chip",
          children: [jsx10(Image, {
            className: "stf-av",
            src: avatar,
            alt: "",
            width: 22,
            height: 22
          }), t("chip_backed", {
            name
          }), " ", jsx10("span", {
            className: "stf-chip-amt",
            children: format.money(NUMBERS.fundingStep)
          })]
        }, key);
      })
    });
    $[0] = assets.avatars.dan;
    $[1] = assets.avatars.maya;
    $[2] = assets.avatars.priya;
    $[3] = assets.avatars.you;
    $[4] = format;
    $[5] = t;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1;
}

// src/stage-overlays.tsx
import { Fragment as Fragment2, jsx as jsx11, jsxs as jsxs7 } from "react/jsx-runtime";
function StageOverlays() {
  return /* @__PURE__ */jsxs7(Fragment2, {
    children: [/* @__PURE__ */jsx11("canvas", {
      className: "stf-confetti",
      "data-stf": "confetti"
    }), /* @__PURE__ */jsx11("output", {
      className: "stf-snack",
      "data-stf": "snack",
      "aria-live": "polite",
      children: /* @__PURE__ */jsxs7("div", {
        className: "stf-snack-row",
        children: [/* @__PURE__ */jsx11(Icon, {
          icon: CheckmarkCircle02IconSolid,
          className: "size-icon-lg",
          "aria-hidden": "true"
        }), /* @__PURE__ */jsx11("span", {
          "data-stf": "snack-text"
        })]
      })
    }), /* @__PURE__ */jsx11("div", {
      className: "stf-cursor",
      "data-stf": "cursor",
      "aria-hidden": "true",
      children: /* @__PURE__ */jsx11(Icon, {
        icon: Touch01IconSolid,
        className: "size-icon-xl"
      })
    }), /* @__PURE__ */jsx11("div", {
      className: "stf-inter",
      "data-stf": "inter",
      "aria-live": "polite",
      children: /* @__PURE__ */jsx11("p", {
        className: "stf-inter-text",
        "data-stf": "inter-text"
      })
    })]
  });
}

// src/simulated-trade-stage.tsx
import { jsx as jsx12, jsxs as jsxs8 } from "react/jsx-runtime";
var STAGE_W = 600;
var STAGE_H = 460;
var MODAL_CHROME_HEIGHT = 200;
function SimulatedTradeStage({
  presentation,
  isStatic,
  initialStage = 0,
  onStage,
  controlRef,
  onButton,
  onClose,
  finalLabel,
  viewportReserve = MODAL_CHROME_HEIGHT
}) {
  const {
    t,
    format
  } = presentation;
  const {
    money
  } = format;
  const rootRef = useRef2(null);
  const fitRef = useRef2(null);
  const [pool, setPool] = useState(0);
  const labelTrade = beat => {
    const amount = money(beat[1]);
    const label = beat[2] === "yes" ? t("trade_yes", {
      amount
    }) : t("trade_no", {
      amount
    });
    return [beat[0], label, beat[2]];
  };
  const labels = {
    introLine: t("intro_line"),
    introButton: t("intro_button"),
    stages: {
      1: {
        status: t("stage1_status"),
        action: t("stage1_action"),
        endLine: t("stage1_end")
      },
      2: {
        status: t("stage2_status"),
        action: t("stage2_action"),
        endLine: t("stage2_end")
      },
      3: {
        status: t("stage3_status"),
        action: t("stage3_action"),
        endLine: t("stage3_end")
      },
      4: {
        status: t("stage4_status"),
        action: t("stage4_action"),
        endLine: t("stage4_end")
      },
      5: {
        status: t("stage5_status"),
        action: t("stage5_action"),
        endLine: t("stage5_end")
      },
      6: {
        status: t("stage6_status"),
        action: finalLabel ?? t("stage6_action"),
        endLine: null
      }
    },
    tagProposal: t("tag_proposal"),
    tagLive: t("tag_live"),
    tagResolved: t("tag_resolved"),
    tagResolvedShort: t("tag_resolved_short"),
    sold: t("position_sold"),
    claimed: t("claimed"),
    snackBought: t("snack_bought", {
      amount: money(NUMBERS.positionCost)
    }),
    snackWon: t("snack_won"),
    snackSold: t("snack_sold", {
      amount: money(NUMBERS.sold)
    }),
    trades3: TRADES3.map(labelTrade),
    trades4: TRADES4.map(labelTrade)
  };
  const latest = useRef2({
    labels,
    format,
    onButton,
    onClose,
    onStage
  });
  useEffect(() => {
    latest.current = {
      labels,
      format,
      onButton,
      onClose,
      onStage
    };
  });
  useLayoutEffect2(() => {
    const fit = fitRef.current;
    const root = rootRef.current;
    if (root !== null) {
      const cover = new URL(presentation.assets.cover, document.baseURI).href;
      root.style.setProperty("--market-cover", `url("${cover}")`);
    }
    if (fit === null) return;
    const apply = () => {
      const w = fit.clientWidth;
      if (w === 0) return;
      const k = Math.min(w / STAGE_W, Math.max(0, (window.innerHeight - viewportReserve) / STAGE_H), 1);
      fit.style.setProperty("--stf-k", `${k}`);
      fit.style.setProperty("--stf-fit-h", `${STAGE_H * k}px`);
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(fit);
    window.addEventListener("resize", apply);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", apply);
    };
  }, [viewportReserve, presentation.assets.cover]);
  useEffect(() => {
    const root_0 = rootRef.current;
    if (root_0 === null) return;
    const runner = createSimulatedTradeRunner(root_0, {
      isStatic,
      initialStage,
      labels: () => latest.current.labels,
      format: {
        money: usd => latest.current.format.money(usd),
        compact: usd_0 => latest.current.format.compact(usd_0)
      },
      onPool: setPool,
      onButton: state => latest.current.onButton(state),
      onStage: stage => latest.current.onStage?.(stage),
      onClose: () => latest.current.onClose()
    });
    controlRef.current = {
      next: runner.next
    };
    runner.start();
    return () => {
      controlRef.current = null;
      runner.destroy();
    };
  }, [isStatic, controlRef, initialStage]);
  return /* @__PURE__ */jsx12(PresentationProvider, {
    value: presentation,
    children: /* @__PURE__ */jsx12("div", {
      ref: rootRef,
      className: isStatic ? "stf stf--static" : "stf",
      children: /* @__PURE__ */jsx12("div", {
        ref: fitRef,
        className: "stf-fit",
        children: /* @__PURE__ */jsxs8("div", {
          className: "stf-stage",
          "data-stf": "stage",
          "data-stage": "1",
          children: [/* @__PURE__ */jsx12(StageOverlays, {}), /* @__PURE__ */jsx12(StageChat, {}), /* @__PURE__ */jsx12(StageFundingChips, {
            format
          }), /* @__PURE__ */jsx12(StageCard, {
            format,
            pool,
            isStatic
          })]
        })
      })
    })
  });
}
export { RenderBoundary, SimulatedTradeStage };